"use client";

import Grid from "@afa-shared/afa-components/dist/Grid";
import Heading from "@afa-shared/afa-components/dist/Heading";
import AccordionDetails, { type AccordionItem } from "@components/Accordion";
import { StandardLink } from "@components/Link/StandardLink";
import RichTextRenderer from "@components/RichTextRenderer";
import { useSearchParams } from "next/navigation";

import type { Context } from "types/context/Context";
import type { FaqListItemWithConcepts } from "types/faqFilters";

import faqListStyles from "./faqList.module.css";
import type { FAQListResultType_faqCollection_items } from "./queries/contentfulTypes/FAQListResultType";

/** Minimal FAQ item shape needed by FAQList. */
interface FAQItem {
  sys?: { id?: string } | null;
  question?: string | null;
  answer?: { json: unknown; links?: unknown } | null;
}

/** Minimal block shape accepted by FAQList — intentionally loose to support multiple query sources. */
export interface FAQListResult {
  sys?: { id?: string } | null;
  heading?: string | null;
  text?: { json?: unknown; links?: unknown } | null;
  linkOnFaqList?: {
    url?: string | null;
    linkText?: string | null;
    variant?: string | null;
    buttonVariant?: string | null;
  } | null;
  manualPostsCollection?: { items: FAQItem[] } | null;
  faqListResult?: Array<FAQListResultType_faqCollection_items>;
}

interface FAQListProps {
  faqListBlock: FAQListResult;
  uniqueKey: string;
  context: Context;
}

export default function FAQList({ faqListBlock, uniqueKey, context }: FAQListProps) {
  const searchParams = useSearchParams();
  const filterInsuranceEvent = searchParams.get("insurance") ?? undefined;
  const filterSubject = searchParams.get("subject") ?? undefined;

  const listitems =
    faqListBlock?.faqListResult ??
    ((faqListBlock?.manualPostsCollection?.items ?? []) as unknown as FaqListItemWithConcepts[]);

  const linkOnFaqList = faqListBlock?.linkOnFaqList;
  const url = faqListBlock?.linkOnFaqList?.url || "";
  const linkVariant = linkOnFaqList?.variant?.toLowerCase() === "knapp" ? "button" : "link";
  const buttonStyle =
    linkOnFaqList?.buttonVariant?.toLowerCase() === "primär" ? "primary" : "secondary";
  const faqVariant = faqListBlock?.heading
    ? context?.heading?.childLevel
    : context?.heading?.parentLevel;

  // Build Accordion items (one <details> per FAQ)
  const items: AccordionItem[] = listitems.map((item, i) => ({
    id: `${uniqueKey}-${item?.sys?.id ?? i}-${i}`,
    question: item?.question ?? "",
    expanded: false,
    answer: (
      <div className={faqListStyles.answer}>
        {item?.answer?.json && (
          <RichTextRenderer json={item.answer.json} links={item.answer.links} context={context} />
        )}
      </div>
    ),
  }));

  // Optional intro (title + text) above the list
  const introContent = (
    <>
      {faqListBlock?.heading && (
        <Heading variant={context?.heading?.parentLevel} removeMargin>
          {faqListBlock.heading}
        </Heading>
      )}
      {faqListBlock?.text && (
        <RichTextRenderer
          json={faqListBlock?.text?.json}
          links={faqListBlock?.text?.links}
          context={context}
        />
      )}
    </>
  );

  return (
    listitems?.length > 0 && (
      <div
        key={uniqueKey}
        className={faqListStyles.faqListBlock}
        data-contentful-entry-id={faqListBlock?.sys?.id}
        data-contentful-field-id="name"
      >
        {faqListBlock?.heading && faqListBlock?.text ? (
          <div className={faqListStyles.textWrapper}>{introContent}</div>
        ) : (
          introContent
        )}

        <Grid data-blockname="faq" className={faqListStyles.faqList}>
          <AccordionDetails
            variant="faq"
            items={items}
            headingTag={faqVariant ?? "h3"}
            tracking={{
              event: "faq_click",
              insurance: filterInsuranceEvent || context?.insuranceName,
              sectionTitle:
                faqListBlock?.heading ??
                context?.accordionHeading ??
                context?.sectionBlock?.sectionBlockHeading ??
                context?.pageHeading,
              sectionIndex: undefined,
              subject: filterSubject,
            }}
          />

          {linkOnFaqList && listitems.length > 0 && (
            <Grid direction="row" justifyContent="center">
              <StandardLink
                href={url}
                text={linkOnFaqList?.linkText}
                variant={linkVariant}
                buttonStyle={buttonStyle}
              />
            </Grid>
          )}
        </Grid>
      </div>
    )
  );
}
